import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Preloader from "../../components/Preloader";
import './TicketCard.css';
import { ticketService } from "../../services/TicketService";
import AdjuntosList from "../../components/AdjuntosList";
import { toast } from "react-toastify";
import { Send } from "lucide-react";
import SocketService from '../../config/socket';
import Permiso from "../../router/Permiso";
import { MdAttachFile, MdInsertDriveFile } from "react-icons/md";
import { getBaseUrl } from "../../config/api";
import { AuthContext } from "../../context/AuthContext";

const ConsultarSupport = () => {
    const { token } = useContext(AuthContext);
    const navigate = useNavigate();
    const baseUrl = getBaseUrl();
    const { id } = useParams();
    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [ticket, setTicket] = useState(null);
    const [archivos, setArchivos] = useState([]);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [attachedFile, setAttachedFile] = useState(null);

    const fetchTicket = async () => {
        try {
            const data = await ticketService.getTicketById(token, id);
            setTicket(data.ticket);
            setArchivos(data.archivos || []);
        } catch (error) {
            console.error('Error al obtener el ticket:', error);
            if (error.response?.status === 404) {
                toast.success("Solicitud no encontrada")
                navigate('/admin/soporte');
            }
            if (error.response?.status === 403) {
                toast.success("Usted no tiene permiso para ver esa solicitud")
                navigate('/admin/soporte');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchMessage = async () => {
        try {
            const data = await ticketService.getMessageByTicket(token, id);
            setMessages(data.message);
            await ticketService.marcarMensajesComoLeidos(token, id, 'user');
        } catch (error) {
            console.error('Error al obtener el ticket:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        if (!message.trim() && !attachedFile) {
            return;
        }

        let payloadToSend;
        let urlId = id;

        if (attachedFile) {
            const formData = new FormData();
            formData.append('archivo', attachedFile, attachedFile.name);
            formData.append('message', message.trim());
            formData.append('sender_type', 'agent');
            payloadToSend = formData;
        } else {
            payloadToSend = message.trim();
        }

        try {
            setSending(true);
            await ticketService.sendMessage(token, urlId, payloadToSend);
            setMessage('');
            setAttachedFile(null);
            fetchMessage();
        } catch (error) {
            console.error('Error al enviar mensaje:', error);
        } finally {
            setSending(false);
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('es-VE', {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    useEffect(() => {
        const handleMessageRegistrado = () => {
            fetchMessage();
        };

        const handleConnect = () => {
            SocketService.emit('joinTicket', id);
        };

        // Registrar listeners
        SocketService.socket.on('connect', handleConnect);
        SocketService.socket.on('messageRegistrado', handleMessageRegistrado);

        // Si ya está conectado, unir a la sala inmediatamente
        if (SocketService.socket.connected) {
            handleConnect();
        }

        // Cargar datos
        fetchTicket();
        fetchMessage();

        return () => {
            SocketService.emit('leaveTicket', id);
            SocketService.socket.off('connect', handleConnect);
            SocketService.socket.off('messageRegistrado', handleMessageRegistrado);
        };
    }, [id]);

    const handleBackToList = () => {
        navigate("/admin/soporte");
    };

    const handleAtender = async () => {
        try {
            await ticketService.atenderTicket(token, ticket.id);
            await fetchTicket();
            toast.success("Solicitud asignada correctamente");
        } catch (error) {
            console.error('Error al asignar ticket:', error);
        }
    };

    const handleCloseTicket = async () => {
        try {
            setLoading(true); // si tienes un estado de carga
            await ticketService.cerrarTicket(token, ticket.id); // tu función de servicio
            toast.success("Ticket cerrado correctamente");
            fetchTicket();
        } catch (error) {
            console.error('Error al cerrar el ticket:', error);
            toast.success("No se pudo cerrar la solicitud")
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const allowedTypes = [
            'image/jpeg',
            'image/png',
            'image/gif',
            'application/pdf',
            'application/msword', // .doc
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        ];

        if (file) {
            if (!allowedTypes.includes(file.type)) {
                alert(`Tipo de archivo no permitido: ${file.name}. Solo se permiten imágenes, PDF y documentos de Word.`);
                event.target.value = null;
                return;
            }
            setAttachedFile(file);
            console.log("Archivo adjunto seleccionado:", file.name);
            event.target.value = null;
        }
    };

    const handleExportTicket = async () => {
        try {
            setLoading(true); // si tienes un estado de carga
            const blob = await ticketService.exportarTicket(token, ticket.id); // tu función de servicio
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "ticket.xlsx";
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al exportar el ticket:', error);
            toast.success("No se pudo exportar la solicitud")
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveAttachment = () => {
        setAttachedFile(null);
    };


    const getColorFromName = (name) => {
        const colors = ['#4a90e2', '#50e3c2', '#f5a623', '#d0021b'];
        const index = name.codePointAt(0) % colors.length;
        return colors[index];
    };

    return (
        <div className="ventas-admin-content">
            {loading && <Preloader />}
            <div className="breadcrumb-container">
                <button type="button" className="breadcrumb-link" onClick={handleBackToList}>
                    ← Listado de solicitudes
                </button>
                <span className="breadcrumb-current"> / Solicitud # {id}</span>
            </div>
            <div className="layout-container">
                {/* Izquierda: contenido principal */}
                <div className="main-content-ticket">
                    <div className="ticket-container">
                        <div className="ticket-card">
                            <div className="ticket-header">
                                <div className="ticket-title-group">
                                    <p className="ticket-title">{ticket?.descripcion_solicitud}</p>

                                </div>
                                <Permiso permiso="export_chat">
                                    {ticket?.id_usuario_asignado && ticket?.id_estado_ticket == 3 && (
                                        <button className="ticket-button green" onClick={handleExportTicket}>
                                            <span className="truncate">Exportar</span>
                                        </button>
                                    )}
                                </Permiso>
                                <Permiso permiso="soporte_asignar">
                                    {ticket?.id_usuario_asignado && ticket?.id_estado_ticket !== 3 && (
                                        <button className="ticket-button green" onClick={handleCloseTicket}>
                                            <span className="truncate">Cerrar Solicitud</span>
                                        </button>
                                    )}
                                </Permiso>
                            </div>

                            <div className="ticket-section">
                                <h3 className="ticket-section-title">Descripción</h3>
                                <p className="ticket-description">
                                    {ticket?.descripcion}
                                </p>
                            </div>

                            <div className="ticket-details">
                                <div>
                                    <p className="label">Solicitado Por</p>
                                    <p className="value">{ticket?.usuario_documento} - {ticket?.usuario_nombre} {ticket?.usuario_apellido} </p>
                                </div>
                                <div>
                                    <p className="label">Fecha de Solicitud</p>
                                    <p className="value">{new Date(ticket?.fecha_creacion).toLocaleString('es-PE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: true
                                    })}</p>
                                </div>
                                <div>
                                    <p className="label">Atendido</p>
                                    <p className="value">{ticket?.asignado_nombre} {ticket?.asignado_apellidoPaterno} {ticket?.asignado_apellidoMaterno}</p>
                                </div>
                                <div>
                                    <p className="label">Fecha de atención</p>
                                    <p className="value">
                                        {ticket?.fecha_atencion
                                            ? new Date(ticket.fecha_atencion).toLocaleString("es-PE", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: true
                                            })
                                            : ""}
                                    </p>

                                </div>
                                <div>
                                    <p className="label">Estatus</p>
                                    <p className="value">{ticket?.estado_descripcion}</p>
                                </div>
                                <div>
                                    <p className="label">Fecha de cierre</p>
                                    <p className="value">
                                        {ticket?.fecha_cierre
                                            ? new Date(ticket.fecha_cierre).toLocaleString("es-PE", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                hour12: true
                                            })
                                            : ""}
                                    </p>

                                </div>

                                <AdjuntosList adjuntos={archivos} />

                            </div>
                            <Permiso permiso="cerrar_solicitud_soporte">
                                {!ticket?.id_usuario_asignado && (
                                    <div className="ticket-actions">
                                        <button className="ticket-button primary" onClick={handleAtender}>
                                            <span className="truncate">Atender</span>
                                        </button>
                                    </div>
                                )}
                            </Permiso>

                        </div>
                    </div>
                </div>

                <div className="conversation-panel">
                    <div className="conversation-card">
                        <div className="conversation-header">
                            <h3>Conversación</h3>
                        </div>

                        <div className="conversation-body">
                            {messages.map((msg) => (
                                <div
                                    key={msg.id || msg.created_at}
                                    className={`message-row ${msg.sender_type === 'agent' ? 'agent' : ''}`}
                                >
                                    {/* Avatar del usuario o agente */}
                                    {msg.sender_type === 'user' && (
                                        <div className="avatar">
                                            {msg.sender_name?.charAt(0).toUpperCase()}
                                        </div>
                                    )}

                                    {/* Burbuja del mensaje */}
                                    <div className={`message-bubble ${msg.sender_type}`}>
                                        {msg.file_url && (
                                            <div className="message-attachment">

                                                {msg.file_url.match(/\.(jpeg|jpg|png|gif|webp)$/i) ? (
                                                    <button
                                                        type="button"
                                                        className="image-container"
                                                        onClick={() => window.open(`${baseUrl}${msg.file_url}`, '_blank')}
                                                        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                                                    >
                                                        <img
                                                            src={`${baseUrl}${msg.file_url}`}
                                                            alt={`Adjunto - ${msg.file_url.split('/').pop()}`}
                                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                                                        />
                                                    </button>
                                                ) : (
                                                    // 2. Enlace de Descarga para Otros Archivos (PDF, DOCX, etc.)
                                                    <a
                                                        href={`${baseUrl}${msg.file_url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="file-download-link"
                                                    >
                                                        <MdInsertDriveFile size={24} style={{ marginRight: '8px' }} />
                                                        **Descargar:** {msg.file_url.split('/').pop()}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        <p>{msg.mensaje}</p>
                                        <span>{msg.sender_name} - {formatTime(msg.created_at)}</span>
                                    </div>

                                    {msg.sender_type === 'agent' && (
                                        <div className="avatar" style={{ backgroundColor: getColorFromName(msg.sender_name) }}>
                                            {msg.sender_name?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div ref={scrollRef} />
                        </div>

                        <div className="conversation-footer">
                            {attachedFile && (
                                <div className="attachment-preview">
                                    <p className="attachment-filename">
                                        {attachedFile.type.startsWith('image/') ? '🖼️ Imagen:' : '📄 Archivo:'}

                                        **{attachedFile.name}** {/* Opcional: Mostrar el tamaño del archivo */}
                                        <span className="file-size">({(attachedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                                    </p>

                                    <button
                                        onClick={handleRemoveAttachment}
                                        className="remove-attachment-btn"
                                        title="Eliminar adjunto"
                                    >
                                        &times;
                                    </button>
                                </div>
                            )}
                            <div className="input-wrapper">
                                <input type="text" placeholder="Escribe tu mensaje aquí" value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()} disabled={ticket?.id_estado_ticket === 3 || ticket?.id_usuario_asignado == null} />
                                <div className="input-actions">
                                    <input
                                        type="file"
                                        id="file-upload"
                                        onChange={handleFileChange}
                                        className="file-input-hidden"
                                        accept="image/*, .pdf, .doc, .docx"

                                    />
                                    <label htmlFor="file-upload" className="attachment-icon-label">
                                        <MdAttachFile size={19} />
                                    </label>
                                    <button onClick={handleSend} disabled={sending || (!message.trim() && !attachedFile)} ><Send size={18} /></button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default ConsultarSupport;
